import { defineStore } from 'pinia'
import { useStammdatenApi } from '@/composables/stammdatenApiComposable'
import { useSessionStorage } from '@vueuse/core'
import { computed, readonly, ref, watch } from 'vue'
import type { PatientenProfil } from '@/stores/patientenprofile/PatientenProfil'
import type { PartialWithRequiredFields } from '@/models/utils'
import type { Regelgruppe } from '@/stores/regelgruppen/Regelgruppe'
import { useRegelnStore } from '@/stores/regeln/RegelnStore'
import { useRegelgruppenStore } from '@/stores/regelgruppen/RegelgruppenStore'
import type { Regel } from '@/stores/regeln/models/regel'
import { v4 as uuidv4 } from 'uuid'
import type { PatientenProfilMetaResponse } from '@/stores/models/patientenprofile/PatientenProfilMetaResponse'
import { Schwangerschaftsmerkmal } from '@/stores/patientenprofile/Schwangerschaftsmerkmal'
import { ZeitEinheit, type Zeitgrenzen } from '@/models/Enums/Zeitgrenzen'
import { OperandType } from '@/models/Enums/Operand'
import { Geschlecht } from '@/stores/patientenprofile/Geschlecht'
import { useFaecherHistory } from '@/composables/windowHistoryComposable'

interface nameCodeType<T> {
  name: string
  code: T
}

type TranslateFn = (key: string) => string

export const usePatientenProfileStore = defineStore('patientenprofile', () => {
  const stammdatenApi = useStammdatenApi()
  const regelStore = useRegelnStore()
  const regelgruppenStore = useRegelgruppenStore()
  const faecherHistory = useFaecherHistory()

  const patientenProfileState = useSessionStorage<PatientenProfil[]>('patientenprofile', [])
  const selectedItemId = ref<string>()
  const isLoadingState = ref(false)
  const isDetailsLoadingState = ref(false)

  function createPatientenProfilName(patientenProfile: PatientenProfil, t: TranslateFn) {
    let name = ''

    if (patientenProfile.geschlecht) {
      patientenProfile.geschlecht.forEach((x) => (name += String(Geschlecht[x])))
    } else {
      name += t('patientenProfilNaming.allGender')
    }

    if (patientenProfile.schwangerschaftsmerkmal == Schwangerschaftsmerkmal.S) {
      if (name.length > 0) name += '; '

      name += t('patientenProfilNaming.pregnant')
    } else if (patientenProfile.schwangerschaftsmerkmal == Schwangerschaftsmerkmal.N) {
      if (name.length > 0) name += '; '
      name += t('patientenProfilNaming.notPregnant')
    }

    if (patientenProfile.zeitgrenzen) {
      if (name.length > 0) name += '; '
      name += createTimeRangeLabel(patientenProfile.zeitgrenzen, t)
    } else if (name.length > 0) {
      name += '; '
      name += t('patientenProfilNaming.noAge')
    }

    if (name.length === 0) name = t('patientenProfilNaming.all')
    return name
  }

  const calculateAndOr = (zeitgrenze: Zeitgrenzen, t: TranslateFn) => {
    if (zeitgrenze.einheit2 == undefined || zeitgrenze.wert2 == undefined) return t('general.and')
    const val1 = getValueInDays(zeitgrenze.einheit1, zeitgrenze.wert1)
    const val2 = getValueInDays(zeitgrenze.einheit2, zeitgrenze.wert2)

    if (
      (String(OperandType[zeitgrenze.operand1]).includes('>') && val2 < val1) ||
      (String(OperandType[zeitgrenze.operand2!]).includes('>') && val1 < val2)
    )
      return t('general.or')
    return t('general.and')
  }

  const getValueInDays = (time: ZeitEinheit, value: number): number => {
    let multiplier = 1
    switch (time) {
      case ZeitEinheit.Wochen:
        multiplier = 7
        break
      case ZeitEinheit.Monate:
        multiplier = 30.5
        break
      case ZeitEinheit.Jahre:
        multiplier = 356
        break
    }
    return value * multiplier
  }

  const updatePatientenProfileState = (patientenProfil: PatientenProfil) => {
    const index = patientenProfileState.value.findIndex((r) => r.id === patientenProfil.id)
    if (index === -1) {
      return false
    } else {
      patientenProfileState.value[index] = patientenProfil
      return true
    }
  }

  const deleteRegelFromPatientenProfil = (id: string) => {
    const pp = patientenProfileState.value.find((x) => x.regeln.find((r) => r.id == id))
    if (!pp) return
    const regelIndex = pp.regeln.findIndex((x) => x.id == id)

    pp.regeln.filter((x) => x.position > pp.regeln[regelIndex].position).map((x) => x.position--)

    const i = pp.regeln.filter((x) => x.id !== id)
    pp.regeln = i
    updatePatientenProfileState(pp)
  }

  const updateRegelFromPatientenProfil = (regel: Regel, ppId: string, oldRegelId?: string) => {
    const id = oldRegelId ?? regel.id
    const pp = patientenProfileState.value.find((x) => x.id == ppId)
    if (!pp) return
    const regelIndex = pp.regeln.findIndex((x) => x.id == id)
    if (regelIndex == -1) return
    pp.regeln[regelIndex] = regel
    updatePatientenProfileState(pp)
  }

  const addRegelFromPatientenProfil = (regel: Regel, ppId: string) => {
    const pp = patientenProfileState.value.find((x) => x.id == ppId)
    if (!pp) return
    pp.regeln.push(regel)
    updatePatientenProfileState(pp)
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let debounceChange: PartialWithRequiredFields<PatientenProfil, 'id'> | undefined = undefined

  const emitDebouncedChange = (
    patientenProfile: PartialWithRequiredFields<PatientenProfil, 'id'>,
    time: number,
    t: TranslateFn
  ) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      if (debounceChange?.geschlecht && !patientenProfile.geschlecht) {
        patientenProfile.geschlecht = debounceChange.geschlecht
      }
      if (debounceChange?.schwangerschaftsmerkmal && !patientenProfile.schwangerschaftsmerkmal) {
        patientenProfile.schwangerschaftsmerkmal = debounceChange.schwangerschaftsmerkmal
      }
    }
    patientenProfile.sameNames = []
    debounceChange = patientenProfile
    debounceTimer = setTimeout(() => {
      void (async () => {
        const oldItem = patientenProfileState.value.find((i) => i.id === patientenProfile.id)
        if (!oldItem) return
        const newItem: PatientenProfil = { ...oldItem, ...patientenProfile }

        newItem.name = createPatientenProfilName(newItem, t)

        newItem.sameNames = patientenProfileState.value
          .filter((x) => x.name == newItem.name)
          .map((x) => x.name)
        updatePatientenProfileState(newItem)

        const req = stammdatenApi<Regelgruppe>(`Regelgruppen/Patientenprofil`).put(newItem)
        const { error } = req
        await req
        debounceChange = undefined

        if (error.value) {
          updatePatientenProfileState(oldItem) // Rollback bei Fehler
        }

        if (!regelgruppenStore.selectedItem?.id) return
        await regelgruppenStore.fetchRegelgruppeById(regelgruppenStore.selectedItem.id)
      })()
    }, time)
  }

  const mergeProfiles = async (id: string) => {
    const name = patientenProfileState.value.filter((x) => x.id === id)[0].name
    isLoadingState.value = true
    const req = stammdatenApi<PatientenProfil>(`Regelgruppen/Patientenprofil/Merge/${id}`)
      .post()
      .json<PatientenProfil>()
    const { data, error } = req
    await req
    if (!error.value && data.value) {
      patientenProfileState.value = patientenProfileState.value.filter((x) => x.name !== name)
      console.log(patientenProfileState.value)
      patientenProfileState.value.push(data.value)
      await regelgruppenStore.fetchRegelgruppeById(regelgruppenStore.selectedItem!.id)
    }
    isLoadingState.value = false
  }

  const updatePatientenProfil = (
    patientenProfile: PartialWithRequiredFields<PatientenProfil>,
    t: TranslateFn
  ) => {
    if (
      patientenProfile.geschlecht ||
      patientenProfile.schwangerschaftsmerkmal ||
      patientenProfile.zeitgrenzen
    ) {
      emitDebouncedChange(patientenProfile, 200, t) // Debounced Update
    } else {
      emitDebouncedChange(patientenProfile, 0, t)
    }
  }

  const operands: nameCodeType<OperandType>[] = [
    { name: '>', code: OperandType['>'] },
    { name: '<', code: OperandType['<'] },
    { name: '>=', code: OperandType['>='] },
    { name: '<=', code: OperandType['<='] }
  ]
  const times = (t: TranslateFn): nameCodeType<ZeitEinheit>[] => [
    { name: t('time.days'), code: ZeitEinheit.Tage },
    { name: t('time.weeks'), code: ZeitEinheit.Wochen },
    { name: t('time.months'), code: ZeitEinheit.Monate },
    { name: t('time.years'), code: ZeitEinheit.Jahre }
  ]

  const createTimeRangeLabel = (zeitgrenze: Zeitgrenzen, t: TranslateFn) => {
    // Mapping-Funktion für Operands
    const getOperandName = (operand: OperandType): string => {
      const found = operands.find((o) => o.code === operand)
      return found ? found.name : ''
    }

    // Mapping-Funktion für Zeit-Einheiten
    const getTimeUnitName = (einheit: ZeitEinheit): string => {
      const found = times(t).find((t) => t.code === einheit)
      return found ? found.name : ''
    }

    const firstAge =
      zeitgrenze.operand1 !== undefined &&
      zeitgrenze.wert1 !== undefined &&
      zeitgrenze.einheit1 !== undefined
        ? `${getOperandName(zeitgrenze.operand1)} ${zeitgrenze.wert1} ${getTimeUnitName(zeitgrenze.einheit1)}`
        : null

    const secondAge =
      zeitgrenze.operand2 !== null &&
      zeitgrenze.wert2 !== null &&
      zeitgrenze.einheit2 !== null &&
      zeitgrenze.operand2 !== undefined &&
      zeitgrenze.wert2 !== undefined &&
      zeitgrenze.einheit2 !== undefined
        ? `${getOperandName(zeitgrenze.operand2)} ${zeitgrenze.wert2} ${getTimeUnitName(zeitgrenze.einheit2)}`
        : null

    return `${firstAge ?? ''}${
      firstAge && secondAge ? ` ${calculateAndOr(zeitgrenze, t)} ${secondAge}` : ''
    }`
  }

  const addPatientenProfil = async (name: string) => {
    if (!regelgruppenStore.selectedItem) return

    const payload = {
      name,
      regelgruppeId: regelgruppenStore.selectedItem.id
    }

    const temporaryGuid = uuidv4()
    const newPatientenprofil = {
      name: name,
      position: regelgruppenStore.selectedItem.patientenProfile?.length
        ? regelgruppenStore.selectedItem.patientenProfile.length + 1
        : 1,
      conflictingNames: [],
      id: temporaryGuid,
      updateDateTime: undefined,
      regelCount: 0,
      usedAnalysen: [],
      usedKeime: [],
      usedKeimgruppen: []
    } as PatientenProfilMetaResponse

    regelgruppenStore.selectedItem.patientenProfile?.push(newPatientenprofil)
    const req = stammdatenApi<PatientenProfil>('Regelgruppen/Patientenprofil')
      .post(payload)
      .json<PatientenProfil>()
    const { data, error } = req
    await req
    if (error.value || !data.value) {
      regelgruppenStore.selectedItem.patientenProfile =
        regelgruppenStore.selectedItem.patientenProfile?.filter((x) => x.id !== temporaryGuid)
    }
    const pp = regelgruppenStore.selectedItem.patientenProfile?.find((x) => x.id == temporaryGuid)
    if (pp) {
      pp.id = data.value.id
      pp.conflictingNames = data.value.conflictingNames
    }
    selectedItemId.value = data.value.id
  }

  const deletePatientenProfil = async (id: string) => {
    const patientenProfilIndex = patientenProfileState.value.findIndex((r) => r.id === id)
    const patientenProfilToDelete = {
      ...patientenProfileState.value.at(patientenProfilIndex)
    } as PatientenProfil
    if (patientenProfilToDelete)
      patientenProfileState.value = patientenProfileState.value.filter((x) => x.id !== id)
    const ppInRg = { ...regelgruppenStore.selectedItem?.patientenProfile?.find((x) => x.id == id) }
    if (ppInRg) {
      regelgruppenStore.selectedItem!.patientenProfile =
        regelgruppenStore.selectedItem!.patientenProfile?.filter((x) => x.id !== id)
      regelgruppenStore
        .selectedItem!.patientenProfile?.filter((x) => x.position > ppInRg.position)
        .forEach((x) => x.position--)
    }
    const { error } = await stammdatenApi(`Regelgruppen/Patientenprofil/${id}`).delete()

    if (error.value) {
      if (patientenProfilToDelete)
        patientenProfileState.value.splice(patientenProfilIndex, 0, patientenProfilToDelete)
    }
    if (!regelgruppenStore.selectedItem?.id) return
    await regelgruppenStore.fetchRegelgruppeById(regelgruppenStore.selectedItem.id)
  }

  const copyPatientenProfil = async (id: string, name: string, position: number) => {
    const regelgruppenId = regelgruppenStore.selectedItem?.id
    if (!regelgruppenId) return
    const pp = regelgruppenStore.selectedItem?.patientenProfile?.find((x) => x.id == id)
    if (!pp) return
    const temporaryGuid = uuidv4()
    const newPatientenprofil = { ...pp }
    newPatientenprofil.id = temporaryGuid
    newPatientenprofil.name = name
    newPatientenprofil.position = regelgruppenStore.selectedItem.patientenProfile?.length
      ? regelgruppenStore.selectedItem.patientenProfile.length + 1
      : 0
    regelgruppenStore.selectedItem.patientenProfile?.push(newPatientenprofil)

    const req = stammdatenApi<PatientenProfil>(`Regelgruppen/Patientenprofil/Copy/${id}`)
      .post({ name, position })
      .json<PatientenProfil>()
    const { data, error, isFetching } = req
    const unwatch = watch(isFetching, (v) => (isLoadingState.value = v))
    await req
    unwatch()
    if (error.value || !data.value) {
      regelgruppenStore.selectedItem.patientenProfile =
        regelgruppenStore.selectedItem.patientenProfile?.filter((x) => x.id !== temporaryGuid)
    }
    const res = regelgruppenStore.selectedItem.patientenProfile?.find((x) => x.id == temporaryGuid)
    if (res) {
      res.id = data.value.id
      res.conflictingNames = data.value.conflictingNames
    }
    selectedItemId.value = data.value.id
  }

  const selectPatientenProfile = async (id: string | undefined) => {
    if (id === selectedItemId.value) return
    if (!id) {
      selectedItemId.value = undefined
      faecherHistory.updateHistory()
      return
    }
    selectedItemId.value = id

    await fetchPatientenprofil(id)
    faecherHistory.updateHistory()
  }

  const reloadPatientenProfile = async () => {
    if (selectedItemId.value) await fetchPatientenprofil(selectedItemId.value)
  }

  const fetchPatientenprofil = async (id: string) => {
    const req = stammdatenApi<PatientenProfil>(`Regelgruppen/Patientenprofil/${id}`)
      .get()
      .json<PatientenProfil>()
    const { isFetching, data, error } = req
    const unwatch = watch(isFetching, (v) => (isDetailsLoadingState.value = v))
    await req
    unwatch()

    if (error.value || !data.value) return
    const existsInState = updatePatientenProfileState(data.value)

    if (!existsInState) {
      patientenProfileState.value.push(data.value)
    }

    regelStore.handleNewRegelnFromPatientenProfil(data.value.regeln)
  }

  const reorderPatientenProfil = async (id: string, position: number) => {
    const pps = regelgruppenStore.selectedItem?.patientenProfile
    if (!pps) return
    const oldPosition = pps.find((x) => x.id == id)
    if (!oldPosition) return
    if (oldPosition.position > position) {
      pps
        .filter((x) => x.position >= position && x.position < oldPosition.position)
        .map((x) => x.position++)
    } else if (oldPosition.position < position) {
      pps
        .filter((x) => x.position <= position && x.position > oldPosition.position)
        .forEach((x) => x.position--)
    }
    oldPosition.position = position
    const req = stammdatenApi<PatientenProfil[]>(`Regelgruppen/Patientenprofil/reorder`)
      .post({ id, position })
      .json<PatientenProfil[]>()
    const { isFetching, error, data } = req
    const unwatch = watch(isFetching, (v) => (isDetailsLoadingState.value = v))
    await req
    unwatch()

    if (error.value && regelgruppenStore.selectedItem?.id) {
      await regelgruppenStore.fetchRegelgruppeById(regelgruppenStore.selectedItem.id)
    }
    if (!data.value) return
    const reorderedProfile = data.value.find((x) => x.id == id)
    if (reorderedProfile) {
      const ok = updatePatientenProfileState(reorderedProfile)
      if (!ok) {
        patientenProfileState.value = [reorderedProfile, ...patientenProfileState.value]
      }
    }
    data.value.forEach((x) => {
      const pp = regelgruppenStore.selectedItem!.patientenProfile!.find((p) => p.id == x.id)
      if (pp) {
        pp.conflictingNames = x.conflictingNames
      }
    })
  }

  const selectedItem = computed(() => {
    return patientenProfileState.value.find((r) => r.id === selectedItemId.value)
  })

  const isLoading = computed(() => isLoadingState.value && patientenProfileState.value.length === 0)
  const isDetailsLoading = computed(
    () => isDetailsLoadingState.value && selectedItem.value?.regeln.length === 0
  )

  return {
    isLoading: readonly(isLoading),
    isDetailsLoading: readonly(isDetailsLoading),
    patientenProfile: readonly(patientenProfileState),
    selectedItem: selectedItem,
    selectPatientenProfile,
    reloadPatientenProfile,
    updatePatientenProfil,
    addPatientenProfil,
    deletePatientenProfil,
    copyPatientenProfil,
    reorderPatientenProfil,
    deleteRegelFromPatientenProfil,
    updateRegelFromPatientenProfil,
    addRegelFromPatientenProfil,
    mergeProfiles
  }
})
